import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import Debounce from 'utils/Debounce';
import decorators from 'utils/decorators';

import modalsSelector from 'selectors/modals/modalsSelector';

/**
 * Custom hook for address validation with interstice loading state
 *
 * @param {Object} params
 * @param {React.RefObject} params.addressFormRef - Reference to FrictionlessAddressForm
 * @param {Function} params.validateAddress - Address validation function from AddressActions
 * @param {Function} params.onAddressSelected - Callback when address is selected/validated
 * @param {boolean} params.isDefault - Whether the selected address should be marked as default
 * @param {boolean} params.isManualEntry - Whether the address was manually entered
 * @returns {Object} { isHidden, validateAddressForm }
 */
function useAddressValidation({
    addressFormRef, validateAddress, onAddressSelected, onBeforeValidation, isDefault, isManualEntry
}) {
    const [isHidden, setIsHidden] = useState(false);

    const { showAddressVerificationModal } = useSelector(modalsSelector);

    const validateAddressForm = Debounce.preventDoubleClick(e => {
        e?.preventDefault?.();

        const formInstance = addressFormRef?.current;

        onBeforeValidation?.({ isManualEntry });

        if (!formInstance || typeof formInstance.getData !== 'function' || typeof formInstance.validateForm !== 'function') {
            return;
        }

        const { getData, validateForm } = formInstance;
        const isValid = validateForm();

        const successCallbacks = {
            RECOMMENDED: recommendedAddress => {
                const address = recommendedAddress || getData().address;

                onAddressSelected?.({ ...address, isDefault });
            },
            UNVERIFIED: () => {}
        };

        const onCancelCallback = () => {
            // If the user doesn't want to change the address, LET THEM
            onAddressSelected?.({ ...getData().address, isDefault });
        };

        if (isValid) {
            decorators.withInterstice(validateAddress)(getData()?.address, successCallbacks, onCancelCallback, undefined, false, true);
        }
    });

    /**
     * toggle address form modal window when address verification modal appears/hides
     */
    useEffect(() => setIsHidden(showAddressVerificationModal), [showAddressVerificationModal]);

    return {
        isHidden,
        validateAddressForm
    };
}

export default useAddressValidation;
